import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Package, ExternalLink, Copy, Pencil, Link2, ChevronLeft, ChevronRight, Star, MessageSquare, Upload, Trash2, Loader2, Archive, ImagePlus, X, Download, RefreshCw, Eye } from "lucide-react";
import { Link, useLocation } from "wouter";
import type { Product, ProductReview } from "@shared/schema";
import { getQueryFn, apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { useState, useRef, useCallback } from "react";
import { AdImage } from "@/components/ad-image";

interface VariantOption {
  title: string;
  values: string[];
}

interface Variant {
  name: string;
  price: string;
  imageUrl?: string;
  /** Task #2054 — all gallery images Shopify associates with this variant. */
  imageUrls?: string[];
  description?: string;
}

function AdgenImageRefsSection({ productId, currentRefs }: { productId: string; currentRefs: string[] }) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const uploadFiles = useCallback(async (files: FileList | File[]) => {
    const fileArr = Array.from(files).filter(f => /\.(jpg|jpeg|png|gif|webp)$/i.test(f.name));
    if (fileArr.length === 0) {
      toast({ title: "Only image files (jpg, png, gif, webp) are allowed", variant: "destructive" });
      return;
    }
    setUploading(true);
    try {
      const uploadedUrls: string[] = [];
      for (const file of fileArr) {
        const formData = new FormData();
        formData.append("file", file);
        const res = await fetch("/api/upload", { method: "POST", body: formData, credentials: "include" });
        if (!res.ok) throw new Error("Upload failed");
        const data = await res.json();
        uploadedUrls.push(data.url || data.falUrl);
      }
      const updated = [...currentRefs, ...uploadedUrls];
      await apiRequest("PATCH", `/api/products/${productId}`, { adgenImageRefs: updated });
      queryClient.invalidateQueries({ queryKey: ["/api/products", productId] });
      toast({ title: `${uploadedUrls.length} image(s) added` });
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message, variant: "destructive" });
    } finally {
      setUploading(false);
    }
  }, [productId, currentRefs, toast]);

  const removeImage = useCallback(async (url: string) => {
    const updated = currentRefs.filter(r => r !== url);
    try {
      await apiRequest("PATCH", `/api/products/${productId}`, { adgenImageRefs: updated });
      queryClient.invalidateQueries({ queryKey: ["/api/products", productId] });
      toast({ title: "Image removed" });
    } catch (e: any) {
      toast({ title: "Remove failed", description: e.message, variant: "destructive" });
    }
  }, [productId, currentRefs, toast]);

  return (
    <div className="fade-in-up-delay-2">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <ImagePlus className="h-4 w-4 text-primary" />
          Adgen Image References
          {currentRefs.length > 0 && (
            <Badge variant="secondary" className="text-[10px] rounded-full">{currentRefs.length}</Badge>
          )}
        </h2>
        <Button
          variant="outline"
          size="sm"
          className="rounded-full"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          data-testid="button-upload-adgen-refs"
        >
          {uploading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />}
          {uploading ? "Uploading..." : "Upload Images"}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files && e.target.files.length > 0) {
              uploadFiles(e.target.files);
              e.target.value = "";
            }
          }}
        />
      </div>

      {currentRefs.length === 0 ? (
        <div
          className={`rounded-xl border-2 border-dashed p-8 text-center transition-colors cursor-pointer ${
            dragOver ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
          }`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files.length > 0) uploadFiles(e.dataTransfer.files); }}
          data-testid="dropzone-adgen-refs"
        >
          <ImagePlus className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No adgen reference images yet</p>
          <p className="text-xs text-muted-foreground mt-1">Click or drag images here to add references for image generation</p>
        </div>
      ) : (
        <div className="space-y-3">
          <div
            className={`grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3 ${dragOver ? "ring-2 ring-primary/30 rounded-xl p-2" : ""}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files.length > 0) uploadFiles(e.dataTransfer.files); }}
          >
            {currentRefs.map((url, i) => (
              <div key={`${url}-${i}`} className="relative group aspect-square rounded-xl border overflow-hidden bg-muted/30" data-testid={`adgen-ref-${i}`}>
                <img src={url} alt={`Adgen ref ${i + 1}`} className="w-full h-full object-cover" />
                <button
                  onClick={() => removeImage(url)}
                  className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80"
                  data-testid={`button-remove-adgen-ref-${i}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="aspect-square rounded-xl border-2 border-dashed border-border hover:border-primary/40 flex flex-col items-center justify-center gap-1 transition-colors cursor-pointer"
              data-testid="button-add-more-adgen-refs"
            >
              <ImagePlus className="h-5 w-5 text-muted-foreground/50" />
              <span className="text-[10px] text-muted-foreground">Add more</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ReviewsSection({ productId, productName }: { productId: string; productName?: string }) {
  const { toast } = useToast();
  const [jsonInput, setJsonInput] = useState("");
  const [showImport, setShowImport] = useState(false);

  const { data: reviews = [], isLoading } = useQuery<ProductReview[]>({
    queryKey: ["/api/products", productId, "reviews"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const importMutation = useMutation({
    mutationFn: async (jsonText: string) => {
      let parsed;
      try {
        parsed = JSON.parse(jsonText);
      } catch {
        throw new Error("Invalid JSON. Please check your format and try again.");
      }
      if (parsed && !Array.isArray(parsed) && Array.isArray(parsed.reviews)) {
        parsed = parsed.reviews;
      }
      if (!Array.isArray(parsed)) parsed = [parsed];
      const res = await apiRequest("POST", `/api/products/${productId}/reviews`, parsed);
      return res.json();
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ["/api/products", productId, "reviews"] });
      toast({ title: `${Array.isArray(created) ? created.length : 1} review(s) imported` });
      setJsonInput("");
      setShowImport(false);
    },
    onError: (e: Error) => {
      toast({ title: "Import Error", description: e.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (reviewId: string) => {
      await apiRequest("DELETE", `/api/reviews/${reviewId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/products", productId, "reviews"] });
    },
  });

  const deleteAllMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/products/${productId}/reviews`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/products", productId, "reviews"] });
      toast({ title: "All reviews deleted" });
    },
  });

  const avgRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / reviews.filter(r => r.rating).length).toFixed(1)
    : null;

  const sentimentCounts = reviews.reduce((acc, r) => {
    const s = r.sentiment || "unknown";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const sentimentColorMap: Record<string, string> = {
    positive: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
    negative: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
    neutral: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400",
    mixed: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  };

  return (
    <div className="fade-in-up-delay-2">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-primary" />
          Reviews
          {reviews.length > 0 && (
            <Badge variant="secondary" className="text-[10px] rounded-full">{reviews.length}</Badge>
          )}
          {avgRating && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground font-normal ml-1">
              <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
              {avgRating}
            </span>
          )}
        </h2>
        <div className="flex items-center gap-2">
          {reviews.length > 0 && (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-destructive hover:text-destructive"
                onClick={() => deleteAllMutation.mutate()}
                disabled={deleteAllMutation.isPending}
                data-testid="button-delete-all-reviews"
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Clear All
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="rounded-full"
                data-testid="button-export-reviews-csv"
                onClick={() => {
                  const headers = ["Reviewer", "Rating", "Sentiment", "Date", "Date Approximate", "Scent", "Review Text", "External ID"];
                  const rows = reviews.map((r) => [
                    r.reviewer || "",
                    r.rating?.toString() || "",
                    r.sentiment || "",
                    r.date || "",
                    r.dateApproximate ? "yes" : "no",
                    r.scent || "",
                    r.reviewText || "",
                    r.externalId?.toString() || "",
                  ]);
                  const csvContent = [headers, ...rows]
                    .map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(","))
                    .join("\n");
                  const blob = new Blob([csvContent], { type: "text/csv" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `${(productName || "product").replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}-reviews.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
              >
                <Download className="h-3.5 w-3.5 mr-1.5" />
                Export CSV
              </Button>
            </>
          )}
          <Button
            variant="outline"
            size="sm"
            className="rounded-full"
            onClick={() => setShowImport(!showImport)}
            data-testid="button-import-reviews"
          >
            <Upload className="h-3.5 w-3.5 mr-1.5" />
            Import JSON
          </Button>
        </div>
      </div>

      {showImport && (
        <div className="rounded-xl border bg-card p-4 mb-4 space-y-3">
          <p className="text-xs text-muted-foreground">
            Paste a single review, an array of reviews, or a full export file with a "reviews" key. Supported fields: id, reviewer, date, date_approximate, scent, rating, review_text, sentiment.
          </p>
          <Textarea
            value={jsonInput}
            onChange={(e) => setJsonInput(e.target.value)}
            placeholder={`[\n  {\n    "id": 194,\n    "reviewer": "Camille",\n    "date": "2024-01-24",\n    "date_approximate": false,\n    "scent": "Unscented",\n    "rating": 5,\n    "review_text": "Love it!!",\n    "sentiment": "positive"\n  }\n]`}
            className="min-h-[180px] font-mono text-xs resize-y"
            data-testid="textarea-review-json"
          />
          <div className="flex items-center gap-2 justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setShowImport(false); setJsonInput(""); }}
              data-testid="button-cancel-import"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="rounded-full"
              onClick={() => importMutation.mutate(jsonInput)}
              disabled={importMutation.isPending || !jsonInput.trim()}
              data-testid="button-submit-import"
            >
              {importMutation.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              {importMutation.isPending ? "Importing..." : "Import Reviews"}
            </Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-20 w-full rounded-xl" />
          <Skeleton className="h-20 w-full rounded-xl" />
        </div>
      ) : reviews.length === 0 ? (
        <div className="rounded-xl border border-dashed p-8 text-center">
          <MessageSquare className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No reviews yet</p>
          <p className="text-xs text-muted-foreground mt-1">Click "Import JSON" to add reviews</p>
        </div>
      ) : (
        <div className="space-y-3">
          {Object.keys(sentimentCounts).length > 1 && (
            <div className="flex gap-2 flex-wrap">
              {Object.entries(sentimentCounts).map(([sentiment, count]) => (
                <Badge
                  key={sentiment}
                  className={`text-[10px] rounded-full px-2.5 capitalize ${sentimentColorMap[sentiment] || sentimentColorMap.neutral}`}
                >
                  {sentiment} ({count})
                </Badge>
              ))}
            </div>
          )}
          <div className="border rounded-xl overflow-hidden divide-y max-h-[500px] overflow-y-auto">
            {reviews.map((review) => (
              <div key={review.id} className="p-4 hover:bg-muted/30 transition-colors group" data-testid={`card-review-${review.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{review.reviewer || "Anonymous"}</span>
                      {review.rating && (
                        <span className="flex items-center gap-0.5">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star
                              key={i}
                              className={`h-3 w-3 ${i < review.rating! ? "fill-amber-400 text-amber-400" : "text-muted-foreground/20"}`}
                            />
                          ))}
                        </span>
                      )}
                      {review.sentiment && (
                        <Badge className={`text-[9px] rounded-full px-2 py-0 capitalize ${sentimentColorMap[review.sentiment] || sentimentColorMap.neutral}`}>
                          {review.sentiment}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {review.date && <span className="text-[10px] text-muted-foreground">{review.date}</span>}
                      {review.scent && <span className="text-[10px] text-muted-foreground">Scent: {review.scent}</span>}
                    </div>
                    {review.reviewText && (
                      <p className="text-sm text-foreground/80 mt-1.5 leading-relaxed">{review.reviewText}</p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                    onClick={() => deleteMutation.mutate(review.id)}
                    data-testid={`button-delete-review-${review.id}`}
                  >
                    <Trash2 className="h-3 w-3 text-muted-foreground" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface ProductFlatlayRun {
  source?: "video_reference" | "ads_express";
  runId: string;
  runStatus: string;
  createdAt: string | null;
  prompt: string | null;
  rawProductImageUrl: string | null;
  taskId: string | null;
  durableUrl: string | null;
  billedAt: string | null;
  variantName: string | null;
  status: "ready" | "rejected" | "failed" | "generating" | "not_generated";
  maintenanceAllowed: boolean;
}

function ProductFlatlayMasterTools({
  brandId,
  productId,
  selectedVariantName,
}: {
  brandId: string;
  productId: string;
  selectedVariantName: string | null;
}) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const endpoint = `/api/brands/${brandId}/products/${productId}/video-reference-flatlays`;
  const flatlays = useQuery<ProductFlatlayRun[]>({
    queryKey: [endpoint],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: open,
    refetchInterval: (query) =>
      (query.state.data as ProductFlatlayRun[] | undefined)?.some(
        (run) => run.status === "generating",
      )
        ? 3000
        : false,
  });
  const mutate = useMutation({
    mutationFn: async ({ runId, action }: { runId: string; action: "delete" | "regenerate" }) => {
      const path = `/api/brands/${brandId}/video-reference-ads/runs/${runId}/product-flatlay`;
      await apiRequest(action === "delete" ? "DELETE" : "POST", action === "delete" ? path : `${path}/regenerate`);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [endpoint] });
      toast({
        title: variables.action === "delete"
          ? "Product flatlay deleted"
          : "Product flatlay regeneration started",
      });
    },
    onError: (error: Error) =>
      toast({ title: "Could not update flatlay", description: error.message, variant: "destructive" }),
  });
  const visibleFlatlays = (flatlays.data || []).filter((run) =>
    selectedVariantName
      ? run.variantName === selectedVariantName
      : run.variantName == null,
  );
  const triggerLabel = selectedVariantName
    ? `View product flatlays for ${selectedVariantName}`
    : "View product-level flatlays";

  return (
    <>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="h-7 w-7 rounded-full text-muted-foreground/40 hover:text-primary"
        onClick={() => setOpen(true)}
        data-testid="button-master-view-product-flatlays"
        data-variant-name={selectedVariantName || undefined}
        aria-label={triggerLabel}
        title={triggerLabel}
      >
        <Eye className="h-3.5 w-3.5" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
          data-testid="product-flatlay-master-tools"
        >
          <DialogHeader>
            <DialogTitle>Product flatlays</DialogTitle>
            <DialogDescription>
              {selectedVariantName
                ? `Internal generation history for the ${selectedVariantName} variant.`
                : "Internal generation history for runs without a selected variant."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
        {flatlays.isLoading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading flatlays…
          </div>
        ) : flatlays.isError ? (
          <p className="text-xs text-destructive">{(flatlays.error as Error).message}</p>
        ) : visibleFlatlays.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            {selectedVariantName
              ? `No flatlays have been generated for the ${selectedVariantName} variant.`
              : "No product-level flatlays have been generated."}
          </p>
        ) : visibleFlatlays.map((run) => (
          <div key={run.runId} className="space-y-3 rounded-lg border bg-background p-3" data-testid={`product-flatlay-run-${run.runId}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[10px]">{run.status.replace("_", " ")}</Badge>
                <span className="text-[10px] text-muted-foreground">{run.source === "ads_express" ? "Ads Express asset" : "Video Reference run"} {run.runId.slice(0, 8)}</span>
              </div>
              {run.billedAt ? <span className="text-[10px] text-muted-foreground">Billed {new Date(run.billedAt).toLocaleString()}</span> : null}
            </div>
            <div className="grid grid-cols-2 gap-3 sm:max-w-md">
              {([["Raw snapshot", run.rawProductImageUrl], ["Generated flatlay", run.durableUrl]] as Array<[string, string | null]>).map(([label, url]) => (
                <div key={label} className="space-y-1">
                  <p className="text-[10px] text-muted-foreground">{label}</p>
                  {url ? (
                    <a href={url} target="_blank" rel="noreferrer">
                      <img src={url} alt={label} className="aspect-square w-full rounded border bg-white object-contain" />
                    </a>
                  ) : (
                    <div className="flex aspect-square items-center justify-center rounded border bg-muted text-[10px] text-muted-foreground">None</div>
                  )}
                </div>
              ))}
            </div>
            {run.taskId ? <p className="break-all text-[10px] text-muted-foreground">Task: {run.taskId}</p> : null}
            {run.source !== "ads_express" && <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                disabled={!run.maintenanceAllowed || mutate.isPending || !["ready", "rejected", "failed"].includes(run.status)}
                onClick={() => {
                  if (window.confirm("Regenerate this flatlay? This starts a new paid image generation and leaves the raw catalog snapshot unchanged.")) {
                    mutate.mutate({ runId: run.runId, action: "regenerate" });
                  }
                }}
              >
                <RefreshCw className="mr-1 h-3 w-3" /> Regenerate
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 text-xs text-destructive hover:text-destructive"
                disabled={!run.maintenanceAllowed || mutate.isPending || !["ready", "rejected", "failed"].includes(run.status)}
                onClick={() => {
                  if (window.confirm("Delete this generated flatlay? The raw catalog snapshot and finished video will remain unchanged.")) {
                    mutate.mutate({ runId: run.runId, action: "delete" });
                  }
                }}
              >
                <Trash2 className="mr-1 h-3 w-3" /> Delete
              </Button>
            </div>}
          </div>
        ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function ProductDetailPage() {
  const { brandId, productId } = useParams<{ brandId: string; productId: string }>();
  const [selectedImage, setSelectedImage] = useState(0);
  const [visibleMediaCount, setVisibleMediaCount] = useState(24);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  // Task #2054 — variant row selection to view per-variant reference images.
  const [selectedVariantIdx, setSelectedVariantIdx] = useState<number | null>(null);
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();

  const duplicateInFlight = useRef(false);
  const activationInFlight = useRef(false);
  const activateMutation = useMutation({
    mutationFn: async (): Promise<Product> => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/products/${productId}/activate`);
      return res.json();
    },
    onSuccess: (activated) => {
      queryClient.setQueryData(["/api/products", productId], activated);
      void queryClient.invalidateQueries({ queryKey: ["/api/products", productId] });
      void queryClient.invalidateQueries({ queryKey: ["/api/brands", brandId, "products"] });
      // Includes URL keys with query strings and extra key segments, while
      // keeping other brands and product-adjacent endpoints untouched.
      const endpoint = `/api/brands/${brandId}/products`;
      void queryClient.invalidateQueries({
        predicate: ({ queryKey }) => typeof queryKey[0] === "string" &&
          (queryKey[0] === endpoint || queryKey[0].startsWith(`${endpoint}?`)),
      });
      toast({ title: "Product activated", description: "This product is now available in Narrative Engine." });
    },
    onError: (error: Error) => {
      let message = error.message;
      const start = message.indexOf("{");
      if (start >= 0) {
        try { message = JSON.parse(message.slice(start)).message || message; } catch { /* retain network error */ }
      }
      toast({ title: "Activation failed", description: message, variant: "destructive" });
    },
    onSettled: () => { activationInFlight.current = false; },
  });
  const duplicateMutation = useMutation({
    mutationFn: async (): Promise<Product> => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/products/${productId}/duplicate`);
      return res.json();
    },
    onSuccess: (copy) => {
      queryClient.setQueryData(["/api/products", copy.id], copy);
      void queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/products`] });
      void queryClient.invalidateQueries({ queryKey: ["/api/brands", brandId, "products"] });
      toast({ title: "Product duplicated" });
      navigate(`/brands/${brandId}/products/${copy.id}`);
    },
    onError: (error: Error) => {
      let message = error.message;
      const jsonStart = message.indexOf("{");
      if (jsonStart >= 0) {
        try { message = JSON.parse(message.slice(jsonStart)).message || message; } catch { /* retain network message */ }
      }
      toast({ title: "Duplication failed", description: message, variant: "destructive" });
    },
    onSettled: () => { duplicateInFlight.current = false; },
  });

  const duplicateProduct = () => {
    if (duplicateInFlight.current) return;
    duplicateInFlight.current = true;
    duplicateMutation.mutate();
  };

  const archiveMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/products/${productId}/archive`);
      return res.json();
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/products`] });
      await queryClient.invalidateQueries({ queryKey: ["/api/products", productId] });
      toast({ title: "Product archived" });
      navigate(`/brands/${brandId}/products`);
    },
    onError: (e: Error) => {
      toast({ title: "Archive failed", description: e.message, variant: "destructive" });
    },
  });

  const { data: product, isLoading } = useQuery<Product>({
    queryKey: ["/api/products", productId],
    queryFn: getQueryFn({ on401: "returnNull" }),
  });

  const bundleProductIds = ((product?.bundleProductIds) as string[] | null) || [];

  const { data: allProducts } = useQuery<Product[]>({
    queryKey: ["/api/brands", brandId, "products"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: !!product?.isBundle && bundleProductIds.length > 0,
  });

  if (isLoading) {
    return (
      <div className="p-8 max-w-5xl mx-auto">
        <Skeleton className="h-8 w-48 mb-6" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <Skeleton className="aspect-square rounded-xl" />
          <div className="space-y-4">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-6 w-1/4" />
            <Skeleton className="h-24 w-full" />
          </div>
        </div>
      </div>
    );
  }
  if (!product) return <div className="p-8">Product not found</div>;

  const media = (product.mediaJson as string[] | null) || [];
  const variantOptions = (product.variantOptionsJson as VariantOption[] | null) || [];
  const variants = (product.variantsJson as Variant[] | null) || [];
  const bundleProducts = (allProducts || []).filter((p) => bundleProductIds.includes(p.id));

  const jsonList = (items: any) => {
    if (!items || (Array.isArray(items) && items.length === 0)) return null;
    const list = Array.isArray(items) ? items : [items];
    return (
      <div className="flex flex-wrap gap-2">
        {list.map((item: any, i: number) => (
          <Badge key={i} variant="secondary" className="text-xs rounded-full">
            {typeof item === "string" ? item : JSON.stringify(item)}
          </Badge>
        ))}
      </div>
    );
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto space-y-8">
      <div className="flex items-center gap-3 fade-in-up">
        <Link href={`/brands/${brandId}/products`}>
          <Button variant="ghost" size="icon" className="rounded-full" data-testid="button-back-products">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div className="flex-1" />
        {product.pdpUrl && (
          <a href={product.pdpUrl} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" size="sm" className="rounded-full" data-testid="link-pdp">
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
              View PDP
            </Button>
          </a>
        )}
        <Link href={`/brands/${brandId}/products/${productId}/edit`}>
          <Button variant="outline" size="sm" className="rounded-full" data-testid="button-edit-product">
            <Pencil className="h-3.5 w-3.5 mr-1.5" />
            Edit
          </Button>
        </Link>
        <Button variant="outline" size="sm" className="rounded-full" data-testid="button-duplicate"
          onClick={duplicateProduct} disabled={duplicateMutation.isPending} aria-busy={duplicateMutation.isPending}>
          {duplicateMutation.isPending
            ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            : <Copy className="h-3.5 w-3.5 mr-1.5" />}
          {duplicateMutation.isPending ? "Duplicating…" : "Duplicate"}
        </Button>
        {user?.isMaster === true ? (
          <ProductFlatlayMasterTools
            brandId={brandId}
            productId={productId}
            selectedVariantName={null}
          />
        ) : null}
        {product.status !== "archived" && (
          <Button
            variant="outline"
            size="sm"
            className="rounded-full text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
            onClick={() => setShowArchiveConfirm(true)}
            data-testid="button-archive-product"
          >
            <Archive className="h-3.5 w-3.5 mr-1.5" />
            Archive
          </Button>
        )}
      </div>

      {product.status === "draft" && !product.archivedAt && (
        <div className="rounded-xl border bg-muted/30 p-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            Draft products are unavailable in Narrative Engine until activated.
          </p>
          <Button
            size="sm"
            className="rounded-full"
            data-testid="button-activate-product"
            disabled={activateMutation.isPending}
            aria-busy={activateMutation.isPending}
            onClick={() => {
              if (activationInFlight.current) return;
              activationInFlight.current = true;
              activateMutation.mutate();
            }}
          >
            {activateMutation.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            {activateMutation.isPending ? "Activating…" : "Activate"}
          </Button>
        </div>
      )}

      {showArchiveConfirm && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 flex items-center justify-between gap-4 fade-in-up">
          <div>
            <p className="text-sm font-medium">Archive this product?</p>
            <p className="text-xs text-muted-foreground mt-0.5">This will mark the product as archived. It won't appear in active product lists.</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowArchiveConfirm(false)}
              data-testid="button-cancel-archive"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              className="rounded-full"
              onClick={() => archiveMutation.mutate()}
              disabled={archiveMutation.isPending}
              data-testid="button-confirm-archive"
            >
              {archiveMutation.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              {archiveMutation.isPending ? "Archiving..." : "Yes, Archive"}
            </Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 fade-in-up">
        <div className="space-y-3">
          {media.length > 0 ? (
            <>
              <div className="relative aspect-square rounded-xl border overflow-hidden bg-muted/30">
                <AdImage
                  src={media[selectedImage]}
                  thumbWidth={960}
                  alt={product.name}
                  className="w-full h-full object-contain p-4"
                  placeholderClassName="!min-h-0 !min-w-0 text-center"
                  data-testid="img-hero"
                />
                {media.length > 1 && (
                  <>
                    <Button
                      variant="secondary"
                      size="icon"
                      className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full opacity-80 hover:opacity-100"
                      onClick={() => setSelectedImage((selectedImage - 1 + media.length) % media.length)}
                      data-testid="button-prev-image"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="secondary"
                      size="icon"
                      className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full opacity-80 hover:opacity-100"
                      onClick={() => setSelectedImage((selectedImage + 1) % media.length)}
                      data-testid="button-next-image"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
              {media.length > 1 && (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2" data-testid="product-detail-media-thumbnails">
                  {media.slice(0, visibleMediaCount).map((url, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedImage(i)}
                      className={`w-16 h-16 rounded-lg border-2 overflow-hidden bg-muted/30 transition-all ${
                        i === selectedImage ? "border-primary ring-1 ring-primary/30" : "border-transparent hover:border-border"
                      }`}
                      data-testid={`img-thumb-${i}`}
                    >
                      <AdImage src={url} thumbWidth={128} alt={`Product photo ${i + 1}`} className="w-full h-full object-cover" placeholderClassName="!min-h-0 !min-w-0 text-center [&_svg]:h-4 [&_svg]:w-4" />
                    </button>
                  ))}
                  </div>
                  <p className="text-xs text-muted-foreground">Photo {selectedImage + 1} of {media.length}</p>
                  {media.length > visibleMediaCount && (
                    <Button type="button" size="sm" variant="outline" onClick={() => setVisibleMediaCount((count) => count + 24)} data-testid="button-more-detail-media">
                      Show 24 more photos ({media.length - visibleMediaCount} remaining)
                    </Button>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="aspect-square rounded-xl border bg-muted/30 flex items-center justify-center">
              <Package className="h-16 w-16 text-muted-foreground/30" />
            </div>
          )}
        </div>

        <div className="space-y-5">
          <div>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              {product.productRole && (
                <Badge variant="secondary" className="text-[10px] capitalize rounded-full">{product.productRole}</Badge>
              )}
              {product.category && (
                <span className="text-xs text-muted-foreground uppercase tracking-wider">{product.category}</span>
              )}
              <Badge variant={product.status === "active" ? "default" : "secondary"} className="text-[10px] rounded-full px-2.5">
                {product.status}
              </Badge>
              {product.isBundle && (
                <Badge variant="outline" className="text-[10px] rounded-full px-2.5 gap-1">
                  <Link2 className="h-3 w-3" />
                  Bundle
                </Badge>
              )}
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight mt-2" data-testid="text-product-name">
              {product.name}
            </h1>
            {product.priceText && (
              <p className="text-xl font-semibold mt-2" data-testid="text-product-price">
                ${product.priceText}
              </p>
            )}
            {product.sku && (
              <p className="text-xs text-muted-foreground mt-1" data-testid="text-product-sku">SKU: {product.sku}</p>
            )}
          </div>

          {product.description && (
            <div className="border-t pt-4">
              <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap" data-testid="text-product-description">
                {product.description}
              </p>
            </div>
          )}

          {variantOptions.length > 0 && (
            <div className="border-t pt-4 space-y-3">
              {variantOptions.map((opt, oi) => (
                <div key={oi}>
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">{opt.title || "Options"}</p>
                  <div className="flex flex-wrap gap-2">
                    {opt.values.map((val, vi) => (
                      <div
                        key={vi}
                        className="px-3 py-1.5 rounded-full border text-sm font-medium hover:bg-muted/50 cursor-default transition-colors"
                        data-testid={`chip-option-${oi}-${vi}`}
                      >
                        {val}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {product.summary && (
            <div className="border-t pt-4">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Summary</p>
              <p className="text-sm text-foreground/80 leading-relaxed">{product.summary}</p>
            </div>
          )}

          {product.jobToBeDone && (
            <div className="border-t pt-4">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Job to Be Done</p>
              <p className="text-sm text-foreground/80 leading-relaxed">{product.jobToBeDone}</p>
            </div>
          )}

          {product.pdpUrl && (
            <div className="border-t pt-4">
              <a
                href={product.pdpUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline flex items-center gap-1.5"
                data-testid="link-pdp-inline"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                View product page
              </a>
            </div>
          )}
        </div>
      </div>

      {variants.length > 0 && (
        <div className="fade-in-up-delay-1">
          <h2 className="text-sm font-semibold mb-3">Variants</h2>
          <div className="border rounded-xl overflow-hidden">
            {variants.map((variant, i) => (
              <div key={i} className="relative border-b last:border-b-0">
                <button
                  type="button"
                  onClick={() => setSelectedVariantIdx(selectedVariantIdx === i ? null : i)}
                  className={`w-full text-left flex items-start gap-3 px-5 py-3.5 pr-14 transition-colors cursor-pointer ${
                    selectedVariantIdx === i ? "bg-primary/5 ring-1 ring-inset ring-primary/30" : "hover:bg-muted/30"
                  }`}
                  data-testid={`row-variant-${i}`}
                >
                  {variant.imageUrl ? (
                    <img src={variant.imageUrl} alt={variant.name} className="h-12 w-12 rounded-lg object-cover shrink-0" data-testid={`img-variant-${i}`} />
                  ) : (
                    <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center shrink-0">
                      <Package className="h-5 w-5 text-muted-foreground" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium">{variant.name}</span>
                      <div className="flex items-center gap-2 shrink-0">
                        {(variant.imageUrls?.length ?? 0) > 0 && (
                          <Badge variant="secondary" className="text-[10px] rounded-full">
                            {variant.imageUrls!.length} {variant.imageUrls!.length === 1 ? "image" : "images"}
                          </Badge>
                        )}
                        <span className="text-sm text-muted-foreground">{variant.price ? `$${variant.price}` : "-"}</span>
                      </div>
                    </div>
                    {variant.description && (
                      <p className="text-xs text-muted-foreground mt-0.5">{variant.description}</p>
                    )}
                  </div>
                </button>
                {user?.isMaster === true ? (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <ProductFlatlayMasterTools
                      brandId={brandId}
                      productId={productId}
                      selectedVariantName={variant.name}
                    />
                  </div>
                ) : null}
              </div>
            ))}
          </div>
          {selectedVariantIdx != null && variants[selectedVariantIdx] && (
            <div className="mt-3" data-testid="variant-ref-images">
              <h3 className="text-xs font-semibold text-muted-foreground mb-2">
                Reference images — {variants[selectedVariantIdx].name}
              </h3>
              {(variants[selectedVariantIdx].imageUrls?.length ?? 0) > 0 ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                  {variants[selectedVariantIdx].imageUrls!.map((url, j) => (
                    <div key={`${url}-${j}`} className="aspect-square rounded-xl border overflow-hidden bg-muted/30" data-testid={`variant-ref-image-${j}`}>
                      <img src={url} alt={`${variants[selectedVariantIdx].name} ref ${j + 1}`} className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-dashed p-6 text-center" data-testid="variant-ref-images-empty">
                  <ImagePlus className="h-6 w-6 text-muted-foreground/30 mx-auto mb-1.5" />
                  <p className="text-xs text-muted-foreground">No reference images associated with this variant</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {product.isBundle && bundleProducts.length > 0 && (
        <div className="fade-in-up-delay-1">
          <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Link2 className="h-4 w-4 text-primary" />
            Bundle Contents
            <Badge variant="secondary" className="text-[10px] rounded-full">{bundleProducts.length}</Badge>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {bundleProducts.map((bp) => {
              const bpMedia = (bp.mediaJson as string[] | null) || [];
              return (
                <Link key={bp.id} href={`/brands/${brandId}/products/${bp.id}`}>
                  <Card
                    className="border-border/50 hover:shadow-md cursor-pointer transition-all hover:-translate-y-0.5"
                    data-testid={`card-bundle-product-${bp.id}`}
                  >
                    <CardContent className="p-4 flex items-center gap-3">
                      {bpMedia.length > 0 ? (
                        <img src={bpMedia[0]} alt={bp.name} className="h-14 w-14 rounded-lg object-cover shrink-0" />
                      ) : (
                        <div className="h-14 w-14 rounded-lg bg-muted flex items-center justify-center shrink-0">
                          <Package className="h-6 w-6 text-muted-foreground" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate" data-testid={`text-bundle-product-name-${bp.id}`}>{bp.name}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                          {bp.category && <span>{bp.category}</span>}
                          {bp.priceText && <span className="font-medium">${bp.priceText}</span>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 fade-in-up-delay-2">
        {!!product.ingredientsOrFeatures && (
          <div className="rounded-xl border p-5">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">Ingredients / Features</p>
            {jsonList(product.ingredientsOrFeatures)}
          </div>
        )}
        {!!product.useCases && (
          <div className="rounded-xl border p-5">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">Use Cases</p>
            {jsonList(product.useCases)}
          </div>
        )}
        {!!product.objections && (
          <div className="rounded-xl border p-5">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">Objections</p>
            {jsonList(product.objections)}
          </div>
        )}
        {!!product.proofPoints && (
          <div className="rounded-xl border p-5">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">Proof Points</p>
            {jsonList(product.proofPoints)}
          </div>
        )}
      </div>

      <AdgenImageRefsSection productId={productId} currentRefs={(product.adgenImageRefs as string[] | null) || []} />

      <ReviewsSection productId={productId} productName={product?.name} />
    </div>
  );
}
